// noinspection JSUnusedGlobalSymbols

import * as React from 'react'
import { createRef, FormEvent, ReactElement, RefObject, useEffect, useMemo, useState } from 'react'
import {
    ComboBox,
    DirectionalHint,
    FontSizes,
    getTheme,
    IButtonProps,
    IComboBox,
    IComboBoxOption,
    IComboBoxProps,
    IconButton,
    IDropdownOption,
    IOnRenderComboBoxLabelProps,
    ISelectableOption,
    IStyle,
    mergeStyles,
    mergeStyleSets,
    SelectableOptionMenuItemType,
    Stack,
} from '@fluentui/react'
import { useConst } from '@fluentui/react-hooks'
import { regexSearchable } from '../../helpers/regexHelper'
import useViewport from '../../hooks/useViewport'
import { listTextFieldStyles } from '../TextField/List/ListTextField'
import { I18nNomenclature } from '../../config/i18nConfig'

export type ComboBoxOption<D = any> = Omit<IComboBoxOption, 'data'> & { data?: D }
export type DropdownOption<D = any> = Omit<IDropdownOption, 'data'> & { data?: D }

export type DropdownProps<D = any> = Omit<IComboBoxProps, 'options' | 'onPendingValueChanged' | 'onMenuDismiss' | 'onMenuOpen'> & {
    className?: string
    readOnly?: boolean
    autoFocus?: boolean
    getSearchableText?: (option: ComboBoxOption<D>, query: string) => string
    getDisplayText?: (options: ComboBoxOption<D>[]) => string
    setSelectedKey?: (option?: ComboBoxOption<D>, forceSelect?: boolean) => Promise<void> | void
    options: ComboBoxOption<D>[]
    list?: boolean
    canClear?: boolean
    onClear?: (currentOptions: ComboBoxOption<D>[]) => void
    clearButtonStyles?: IButtonProps['styles']
}

export function sortOptions<O extends ISelectableOption = ISelectableOption>(
    asc: boolean = true,
    getValue: (option: O) => string | number = (option: O) => option.text,
): (a: O, b: O) => number {
    // Make sure all items are sorted alphabetically.
    return (a: O, b: O) => getValue(a).toLocaleString().localeCompare(getValue(b)?.toString()) * (asc ? 1 : -1)
}

export function reverseSortOptions<O extends ISelectableOption = ISelectableOption>(
    getValue?: (option: O) => string | number,
): (a: O, b: O) => number {
    // Make sure all items are sorted non-alphabetically.
    return sortOptions<O>(false, getValue)
}

export function mapSortedEntities<E extends I18nNomenclature = I18nNomenclature, O extends ISelectableOption = ISelectableOption>(
    entities: E[],
    map: (entity: E) => O,
): O[] {
    return [
        ...entities
            .filter((entity: E) => entity.position)
            .sort((a: E, b: E) => a.position - b.position)
            .map(map),
        {
            key: 'divider',
            text: '-',
            itemType: SelectableOptionMenuItemType.Divider,
        } as O,
        ...entities
            .filter((entity: E) => !entity.position)
            .map(map)
            .sort(sortOptions()),
    ]
}

export function trimDividerOptions<O extends ISelectableOption = ISelectableOption>(options: O[]): O[] {
    return options
        .filter(
            (_option: O, i: number, options: O[]) =>
                i === 0 || ![options[i - 1], options[i]].every(o => o.itemType === SelectableOptionMenuItemType.Divider),
        )
        .filter(
            (option: O, i: number, options: O[]) => (i !== 0 && i !== options.length - 1) || option.itemType !== SelectableOptionMenuItemType.Divider,
        )
}

const theme = getTheme()

const styles = {
    stack: mergeStyles({ position: 'relative' }),
    caretDownButtonList: mergeStyleSets({ root: { width: 24 } }),
    clearButton: mergeStyleSets({
        root: {
            position: 'absolute !important',
            backgroundClip: 'padding-box',
            right: 32,
            border: '1px solid transparent',
            zIndex: 1,
        },
        icon: { fontSize: FontSizes.size12, color: theme.palette.neutralDark },
    }),
    clearButtonList: mergeStyleSets({
        root: mergeStyles(listTextFieldStyles.root, { width: 24, height: 24, right: 24 }),
    }),
    clearButtonBlur: mergeStyleSets({ root: { borderTopWidth: '1px', borderBottomWidth: '1px' } }),
    clearButtonFocus: mergeStyleSets({ root: { borderTopWidth: '2px', borderBottomWidth: '2px' } }),
    dropdown: mergeStyleSets({ container: { flexGrow: 1 } }),
    dropdownClear: mergeStyleSets({ root: { paddingRight: 64, '> .ms-ComboBox-CaretDown-button': { right: 0 } } }),
    dropdownList: mergeStyleSets({
        root: mergeStyles(listTextFieldStyles.root, listTextFieldStyles.fieldGroup, {
            paddingLeft: '0',
            paddingRight: 24,
            ':after': { borderWidth: '0 0 1px 0', height: 24 },
        }),
        rootFocused: {
            ':after': { borderWidth: '0 0 2px 0 !important', marginTop: '-1px' },
        },
        input: mergeStyles(listTextFieldStyles.field),
    }),
    dropdownClearList: mergeStyleSets({ root: { paddingRight: 48 } }),
}

export default function BaseDropdown<D>(props: DropdownProps<D>): ReactElement {
    const [filteredOptions, setFilteredOptions] = useState<ComboBoxOption<D>[]>([])
    const [selectedOptions, setSelectedOptions] = useState<ComboBoxOption<D>[]>([])
    const [query, setQuery] = useState<string>('')
    const [immune, setImmune] = useState<boolean>(false)
    const ref = useConst<RefObject<IComboBox>>(() => (props.componentRef as RefObject<IComboBox>) || createRef<IComboBox>())
    const { height } = useViewport()
    const canClear = useMemo(
        () => ((props.canClear ?? !(props.required ?? true)) || !!props.onClear) && selectedOptions.length > 0,
        [props.canClear, props.onClear, selectedOptions],
    )
    const [clearButtonActionStyle, setClearButtonActionStyle] = useState<IStyle>(styles.clearButtonBlur)

    const dropdownStyles = useMemo<IComboBoxProps['styles']>(
        () =>
            mergeStyleSets(
                props.styles,
                styles.dropdown,
                props.list && styles.dropdownList,
                canClear && styles.dropdownClear,
                props.list && canClear && styles.dropdownClearList,
            ) as IComboBoxProps['styles'],
        [props.styles, props.list, canClear],
    )
    const caretDownButtonStyles = useMemo(
        () => mergeStyleSets(props.caretDownButtonStyles, props.list && styles.caretDownButtonList),
        [props.caretDownButtonStyles, props.list],
    )
    const clearButtonStyles = useMemo(
        () => mergeStyleSets(props.clearButtonStyles, styles.clearButton, clearButtonActionStyle, props.list && styles.clearButtonList),
        [props.clearButtonStyles, props.list, clearButtonActionStyle],
    )

    const filterOptions = (q: string = '') => {
        const query = regexSearchable(q.trim())
        const options = (props.options || []).filter(
            (option: ComboBoxOption) =>
                option.itemType === SelectableOptionMenuItemType.Divider ||
                regexSearchable((props.getSearchableText ? props.getSearchableText(option, query) : option.text) || '').includes(query),
        )
        setFilteredOptions(trimDividerOptions(options))
        setQuery(query)
    }

    useEffect(() => {
        filterOptions()
    }, [props.options])

    useEffect(() => {
        if (ref?.current && !props.selectedKey && props.autoFocus) {
            ref.current.focus(false)
        }
    }, [ref?.current])

    useEffect(() => {
        const selectedKeys = (Array.isArray(props.selectedKey) ? props.selectedKey : [props.selectedKey]).filter(key => key)
        setSelectedOptions((props.options || []).filter(option => selectedKeys.includes(option.key)))
    }, [props.selectedKey, props.options])

    return (
        <Stack horizontal className={styles.stack}>
            <ComboBox
                required
                scrollSelectedToTop
                useComboBoxAsMenuWidth
                autoComplete="off"
                onChange={(_: FormEvent<IComboBox | HTMLElement>, option?: ComboBoxOption) => {
                    if (option) {
                        props.setSelectedKey?.(option)
                        // Declare immunity, so that on menu dismiss this value won't be reverted.
                        setImmune(true)
                    }
                }}
                text={props.getDisplayText?.(selectedOptions)}
                aria-readonly={props.readOnly || !props.options}
                allowFreeform={!props.readOnly && !!props.options}
                {...props}
                styles={dropdownStyles}
                caretDownButtonStyles={caretDownButtonStyles}
                placeholder={!props.options ? 'Bezig met laden...' : props.placeholder}
                calloutProps={{
                    directionalHint: DirectionalHint.bottomAutoEdge,
                    directionalHintFixed: false,
                    calloutMaxHeight: height / 2 - 32,
                    ...(props.calloutProps || {}),
                }}
                componentRef={ref}
                options={filteredOptions}
                onBlur={event => {
                    setClearButtonActionStyle(styles.clearButtonBlur)
                    props.onBlur?.(event)
                }}
                onFocus={event => {
                    setClearButtonActionStyle(styles.clearButtonFocus)
                    props.onFocus?.(event)
                }}
                onMenuOpen={() => (props.readOnly || !props.options) && ref.current.dismissMenu()}
                onMenuDismissed={() => {
                    if (query.length > 0 && filteredOptions.length === 1 && !immune) {
                        props.setSelectedKey?.(filteredOptions[0], true)
                    }
                    setFilteredOptions(props.options)
                    setImmune(false)
                }}
                onPendingValueChanged={(_option?: ComboBoxOption, _index?: number, value?: string) => {
                    if (value !== undefined && !props.readOnly && props.options) {
                        ref.current.focus(true, true)
                        filterOptions(value)
                    }
                }}
                onRenderLabel={(renderProps: IOnRenderComboBoxLabelProps, render) => (
                    <>
                        {render(renderProps)}
                        {canClear && !props.disabled && (
                            <IconButton
                                onClick={() => (props.onClear ? props.onClear(selectedOptions) : props.setSelectedKey?.())}
                                iconProps={{ iconName: 'Clear' }}
                                styles={clearButtonStyles}
                                tabIndex={-1}
                            />
                        )}
                    </>
                )}
            />
        </Stack>
    )
}
